// 2FA Setup Component with QR Code

import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { authService } from '../api/authService';
import './TwoFactorSetup.css';

interface TwoFactorSetupProps {
  onComplete: () => void;
  onCancel: () => void;
  /** Zeigt ganz oben einen deutlichen Button "2FA-Einrichtung überspringen"
   *  (nur für den empfohlenen, nicht erzwungenen Flow). */
  allowSkip?: boolean;
}

export function TwoFactorSetup({ onComplete, onCancel, allowSkip = false }: TwoFactorSetupProps) {
  const [step, setStep] = useState<'loading' | 'qrcode' | 'verify'>('loading');
  const [qrCodeUri, setQrCodeUri] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setupCalled = React.useRef(false);

  React.useEffect(() => {
    // Prevent double-execution in React StrictMode
    if (!setupCalled.current) {
      setupCalled.current = true;
      setup();
    }
  }, []);

  // QR-Code lokal als Data-URL generieren
  useEffect(() => {
    if (qrCodeUri) {
      QRCode.toDataURL(qrCodeUri, { width: 200, margin: 2 })
        .then(url => setQrCodeDataUrl(url))
        .catch(() => setQrCodeDataUrl(''));
    }
  }, [qrCodeUri]);

  const setup = async () => {
    try {
      const data = await authService.setup2FA();
      setQrCodeUri(data.qrCodeUri);
      setSecretKey(data.secretKey);
      setBackupCodes(data.backupCodes || []);
      setStep('qrcode');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Setup fehlgeschlagen');
      setStep('qrcode');
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.enable2FA(verificationCode);
      setStep('verify');
      // Nach 2 Sekunden abschließen
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const downloadBackupCodes = () => {
    const text = `WPClub 2FA Backup Codes\n\n${backupCodes.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wpclub-backup-codes.txt';
    a.click();
  };

  if (step === 'loading') {
    return (
      <div className="twofa-setup-modal">
        <div className="twofa-setup-content">
          <div className="loading-spinner">Laden...</div>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="twofa-setup-modal">
        <div className="twofa-setup-content">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>2FA erfolgreich aktiviert!</h2>
            <p>Ihre Zwei-Faktor-Authentifizierung ist jetzt aktiv.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="twofa-setup-modal" onClick={onCancel}>
      <div className="twofa-setup-content" onClick={(e) => e.stopPropagation()}>
        {allowSkip && (
          <button
            type="button"
            className="skip-2fa-btn"
            onClick={onCancel}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: '12px',
              background: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: '8px',
              color: '#1e40af',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
            }}
          >
            2FA-Einrichtung überspringen →
          </button>
        )}
        <div className="twofa-setup-header">
          <h2>🔐 Zwei-Faktor-Authentifizierung einrichten</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="twofa-setup-body">
          <div className="step">
            <h3>Schritt 1: QR-Code scannen</h3>
            <p>Scannen Sie diesen QR-Code mit Ihrer Authenticator-App (Google Authenticator, Authy, etc.)</p>

            <div className="qr-code-container">
              {qrCodeDataUrl ? (
                <img src={qrCodeDataUrl} alt="QR Code" width={200} height={200} />
              ) : qrCodeUri ? (
                <p style={{ color: 'var(--text-muted)' }}>QR-Code wird generiert...</p>
              ) : null}
            </div>

            <div className="secret-key">
              <label>Oder geben Sie diesen Code manuell ein:</label>
              <div className="key-display">
                <code>{secretKey}</code>
                <button onClick={() => copyToClipboard(secretKey)} className="btn-copy-secret">
                  📋 Kopieren
                </button>
              </div>
            </div>
          </div>

          {backupCodes.length > 0 && (
            <div className="step">
              <h3>Schritt 2: Backup-Codes speichern</h3>
              <p className="warning-message">
                ⚠️ Speichern Sie diese Codes an einem sicheren Ort! Sie können sie verwenden, wenn Sie keinen Zugriff auf Ihre Authenticator-App haben.
              </p>

              <div className="backup-codes-grid">
                {backupCodes.map((code, index) => (
                  <div key={index} className="backup-code">{code}</div>
                ))}
              </div>

              <button onClick={downloadBackupCodes} className="btn-secondary">
                💾 Backup-Codes herunterladen
              </button>
            </div>
          )}

          <div className="step">
            <h3>Schritt {backupCodes.length > 0 ? '3' : '2'}: Code verifizieren</h3>
            <p>Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein:</p>

            <form onSubmit={handleVerify}>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="code-input"
                required
              />

              <div className="button-group">
                <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Überprüfen...' : '2FA aktivieren'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
